import { supabase, isConfigured, requireClient } from './supabase.js';
import { currentUser, ensureSession, isAnonymous } from './auth.js';
import { APP_VERSION } from '../version.js';
import { S } from '../state/store.js';
import { renderer } from '../scene/context.js';

/* Feedback — one insert into a table nobody can read back.

   The whole flow is a single INSERT. There is no email step here and no
   endpoint of mine in the client: supabase/migrations/0006_feedback.sql
   grants insert-only, and supabase/functions/feedback-digest (service-role,
   scheduled) is what turns the rows into one email a day. That keeps the
   mail I receive proportional to the day rather than to the traffic.

   Context is attached automatically. Almost every message worth acting on is
   really a bug report, and the difference between an actionable one and a
   useless one is knowing the build, the view and the calibration — none of
   which a person should have to type, and all of which the client already
   knows. */

/* The standard bug-report questionnaire — OS, browser, graphics, screen — as
   something collected rather than asked. Every one of these is a question a
   support form would normally make the person answer, and every one of them
   is answered badly: people report "Chrome" without a version, "Windows"
   without knowing it's 11, and essentially nobody can name their GPU. The
   browser knows all of it exactly. */

/** Chromium's Client Hints, which give exact OS and browser versions. */
async function platformInfo() {
  const d = navigator.userAgentData;
  /* Firefox and Safari have no userAgentData at all. They aren't this app's
     target (tools/verify.mjs runs Chrome and Edge), but someone will still
     open it in one and their report shouldn't arrive blank. */
  if (!d) return { os: '', browser: '', mobile: '' };
  let hi = {};
  try {
    hi = await d.getHighEntropyValues(['platform', 'platformVersion', 'fullVersionList']);
  } catch (_) { /* rejected or unsupported — the low-entropy fields below still work */ }

  /* brands always contains two pieces of deliberate noise ("Not;A=Brand" and
     similar, varying per release) to stop exactly the kind of sniffing this
     is doing. Pick the real engine's sibling: the first brand that isn't
     Chromium and isn't the decoy. */
  const list = hi.fullVersionList || d.brands || [];
  const real = list.find(b => !/not[^a-z]*a[^a-z]*brand/i.test(b.brand) && b.brand !== 'Chromium')
            || list.find(b => b.brand === 'Chromium');

  /* platformVersion is the NT/Darwin build number, not the marketing name.
     Windows 11 reports 13+ where Windows 10 reports 1–12 — the UA string says
     "Windows NT 10.0" for both, which is why the raw string can't tell them
     apart and this can. */
  let os = hi.platform || d.platform || '';
  const major = parseInt(String(hi.platformVersion || '').split('.')[0], 10);
  if (os === 'Windows' && Number.isFinite(major)) os += major >= 13 ? ' 11' : major > 0 ? ' 10' : '';
  else if (hi.platformVersion) os += ' ' + hi.platformVersion;

  return {
    os,
    browser: real ? `${real.brand} ${real.version}` : '',
    mobile: d.mobile ? 'yes' : 'no'
  };
}

/** What the GPU actually is — for a WebGL app, often the whole answer. */
function graphicsInfo() {
  try {
    const gl = renderer.getContext();
    /* WEBGL_debug_renderer_info is the only way to the real adapter string;
       without it every machine reports a generic vendor and driver-specific
       bugs become unattributable. Chrome may withhold it, so this is a
       best-effort read, not a guarantee. */
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    return {
      gpu: dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : (gl.getParameter(gl.RENDERER) || ''),
      webgl: gl.getParameter(gl.VERSION) || '',
      /* Export supersamples, so the card's ceiling decides whether a chosen
         factor is honoured or silently reduced — a recurring source of "my
         export looks different from the preview". */
      maxTexture: gl.getParameter(gl.MAX_TEXTURE_SIZE) || 0,
      maxViewport: (gl.getParameter(gl.MAX_VIEWPORT_DIMS) || [])[0] || 0
    };
  } catch (_) {
    /* A lost or never-created context is itself worth reporting, and is
       exactly the state someone writes in about. */
    return { gpu: 'unavailable', webgl: '', maxTexture: 0, maxViewport: 0 };
  }
}

/** A count of what is actually in the scene, by kind. */
function objectMix() {
  if (!Array.isArray(S.objects) || !S.objects.length) return '';
  const n = {};
  for (const o of S.objects) n[o.type] = (n[o.type] || 0) + 1;
  return Object.entries(n).map(([k, v]) => `${k}×${v}`).join(', ');
}

/**
 * What the app can say about itself, for a message that arrives without it.
 *
 * Exported because the form shows it. Collecting this quietly would be the
 * easy option and the wrong one: it includes the machine's GPU, timezone and
 * user agent, so the person sending it should be able to read the exact
 * values before they press Send, not a description of them.
 */
export async function snapshot() {
  const { commit, branch, dirty } = APP_VERSION;
  const user = currentUser();
  return {
    env: {
      ...await platformInfo(),
      ...graphicsInfo(),
      /* Window vs. display: a layout bug is usually about the first, and a
         "everything is tiny" report about the second. devicePixelRatio and
         the UI-scale zoom both change what a CSS pixel means, and both are
         things people change and forget. */
      viewport: `${innerWidth}×${innerHeight}`,
      display: `${screen.width}×${screen.height}`,
      dpr: devicePixelRatio,
      uiScale: document.documentElement.style.zoom || '1',
      cores: navigator.hardwareConcurrency || 0,
      memoryGB: navigator.deviceMemory || 0,
      language: navigator.language || '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      online: navigator.onLine ? 'yes' : 'no',
      ua: navigator.userAgent
    },
    app: {
      build: `${commit}${dirty ? '+' : ''} (${branch})`,
      stage: S.stage || '',
      view: S.view || '',
      /* The calibration is the thing that goes wrong, so it travels with
         every report whether or not the report is about it. */
      res: S.res ? `${S.res.w}×${S.res.h}` : '',
      screenSize: S.screen ? `${S.screen.w}×${S.screen.h} m` : '',
      setup: S.photo ? 'reference photo' : 'manual',
      calibrated: S.solved ? 'yes' : 'no',
      objects: Array.isArray(S.objects) ? S.objects.length : 0,
      objectMix: objectMix(),
      supersample: `×${S.ss}`,
      motionBlur: S.motionBlur > 1 ? `×${S.motionBlur}` : 'off',
      previewQuality: S.previewQuality ? 'on' : 'off',
      shadowRes: S.shadowRes,
      lighting: S.lighting || '',
      tone: S.tone || '',
      theme: document.documentElement.dataset.theme || '',
      account: !user ? 'none' : isAnonymous() ? 'anonymous' : 'signed in',
      /* How long they had been working. "It happens after a while" is a real
         and common report shape, and this is the only way to see it. */
      uptimeMin: Math.round(performance.now() / 60000)
    }
  };
}

/**
 * Send a message. Resolves when the row is stored; rejects with something
 * worth showing the person who typed it.
 *
 * @param {string} message  what they wrote
 * @param {string} [replyTo] optional address to answer on
 */
export async function sendFeedback(message, replyTo) {
  const text = String(message || '').trim();
  if (!text) throw new Error('Nothing to send yet.');
  /* Matches the column's check constraint. Catching it here means a long
     message is refused while it can still be edited, rather than after a
     round trip. */
  if (text.length > 4000) throw new Error('That is longer than 4000 characters — trim it a little.');

  if (!isConfigured) throw new Error('Feedback needs Supabase credentials. Email instead — see the README.');

  /* Retried here rather than trusted from boot: initAuth() gives up quietly
     by design, so a visitor can reach this form with no session at all — and
     the row needs a uid. */
  const user = currentUser() || await ensureSession();
  if (!user) throw new Error(
    "Couldn't sign you in to send that. If you're the developer: anonymous sign-ins need enabling in Supabase (Authentication → Sign In / Providers).");

  const { error } = await supabase.from('feedback').insert({
    user_id: user.id,
    message: text,
    reply_to: (replyTo || '').trim() || null,
    context: await snapshot()
  });

  /* The rate-limit trigger raises with a message written to be read by a
     person, so pass it through rather than replacing it with something
     vaguer. Anything else is infrastructure and gets a generic line. */
  if (error) {
    if (error.code === '23514' || /too many/i.test(error.message || '')) throw new Error(error.message);
    console.error('[illusio] feedback insert failed', error);
    throw new Error("Couldn't send that just now. Try again in a moment.");
  }
}


/* ── reading it back ────────────────────────────────────────────────────────
   Everything below is for the owner's inbox view (ui/feedback-inbox.js) and
   works only for an account listed in feedback_admins. For everyone else the
   RLS policy from 0007 simply returns nothing — these calls are safe to make,
   they just come back empty, so the UI never has to guess at permissions. */

/** Is this account allowed to read feedback? Answered by the database, not by
 *  comparing an email in the client — the client's opinion is not what the
 *  policy consults, so asking anything else could disagree with reality. */
export async function isFeedbackAdmin() {
  if (!isConfigured || !currentUser()) return false;
  const { data, error } = await supabase.rpc('is_feedback_admin');
  if (error) { console.warn('[illusio] admin check failed', error.message); return false; }
  return data === true;
}

/**
 * Recent feedback, newest first.
 * @param {{unreadOnly?: boolean, limit?: number}} [opts]
 */
export async function listFeedback(opts = {}) {
  requireClient();
  let q = supabase
    .from('feedback')
    .select('id, message, reply_to, context, created_at, read_at')
    .order('created_at', { ascending: false })
    .limit(opts.limit ?? 100);
  if (opts.unreadOnly) q = q.is('read_at', null);

  const { data, error } = await q;
  if (error) {
    console.error('[illusio] could not read feedback', error);
    throw new Error("Couldn't load feedback. Are you signed in as an admin?");
  }
  /* An empty list from a non-admin is indistinguishable from an empty list
     from an admin with no feedback, which is exactly right: the policy is the
     access control, and the client is not asked to enforce anything. */
  return data ?? [];
}

/** Mark rows read. The trigger in 0007 rejects any other column change, so
 *  this cannot quietly become a way to edit what someone wrote. */
/** Delete one for good (0022). A real delete, not a flag: three separate
 *  readers exist for this table — the inbox, the unread count and the daily
 *  digest — and a soft delete only works while every one of them remembers to
 *  filter for it. The one that forgets is the one that emails you about a row
 *  you thought you had removed. */
export async function deleteFeedback(id) {
  requireClient();
  const { error } = await supabase.from('feedback').delete().eq('id', id);
  if (error) {
    console.error('[illusio] could not delete feedback', error);
    throw new Error("Couldn’t delete that just now.");
  }
}

export async function markRead(ids, read = true) {
  if (!ids?.length) return;
  requireClient();
  const { error } = await supabase
    .from('feedback')
    .update({ read_at: read ? new Date().toISOString() : null })
    .in('id', ids);
  if (error) {
    console.error('[illusio] could not update read state', error);
    throw new Error("Couldn't update that just now.");
  }
}
